import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { FormikProps, Formik, Form, Field } from "formik";
import * as yup from "yup";
import { request } from "../../../../utils/Request";
import { Col, Row } from "react-bootstrap";
import { Sidebar } from "primereact/sidebar";
import { get, isEmpty, last, sortBy } from "lodash";
import { Button } from "primereact/button";
import {  random } from "../../../../utils/Helper";
import { InputSingleSelectField } from "../../../../components/Shared/InputSingleSelectField";
import { InputSwitchField } from "../../../../components/Shared/InputSwitchField";

interface IFormProps {
    t?: any | undefined;
    onClose: any;
    visible: any;
    toastify: any;
    dropdowns: any;
}

type TInputForm = {
    token: any,
    from_location_id: any,
    is_active: any,
    to_location_id: any,
    status: any,
    is_final_journey: any
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        status: yup.string().nullable(),
        is_active: yup.boolean().nullable(),
        from_location_id: yup.string().required(t('the field is required.', { field: t('from location') })).nullable(),
        to_location_id: yup.string().required(t('the field is required.', { field: t('to location') })).nullable(),
        is_final_journey: yup.boolean().nullable()
    });
};

const DEFAULT_FORM_STATE = {
    token: random(5),
    from_location_id: null,
    is_active: false,
    to_location_id: null,
    status: 'pending',
    is_final_journey: false
};

function AddJourneysToOrder(props: IFormProps) {
    const { t, dropdowns, toastify } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<TInputForm>(DEFAULT_FORM_STATE);
    const [panelState, setPanelState] = useState<boolean>(false);
    const [entity, setEntity] = useState<any>(null);

    const onClose = () => {
        setPanelState(false);

        if ( props.onClose ) {
            props.onClose();
        }
    };

    const onSubmit = async (payloads: TInputForm, { setErrors, resetForm }: any) => {
        try {
            const response = await request({
                method: 'POST',
                url: `/suppliers/orders/journey`,
                data: {
                    id: entity.id,
                    ...payloads,
                }
            });

            toastify(t("record updated", { item: t('order') }), "success");
            resetForm();
            onClose();
        } catch (e: any) {
            if ( e.status === 422 ) {
                setErrors(e.data.errors);
                toastify(t("unprocessable entity"), "error");
            } else {
                toastify(t("server error"), "error");
            }
        }
    };
    const onDetail = async (id: string): Promise<void> => {
        try {
            const response = await request({
                url: `/suppliers/orders/${ id }`
            });

            const { data }: any = response.data;
            setEntity(data);
            const mostRecent = last(sortBy(data.order_journeys, [function(o: any) { return o.created_at; }]));

            setInitFormState({
                ...DEFAULT_FORM_STATE,
                from_location_id: get(mostRecent, ['to_location', 'id'])
            });
            setPanelState(true);
        } catch (error: any) {
            toastify(t("server error"), "error");
        }
    };

    useEffect(() => {
        if ( !isEmpty(props.visible) ) {
            onDetail(props.visible);
        }
    }, [props.visible]);

    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t('update') } { t('order') }{ entity && ` #${ entity.order_cd }` }</h5>
                            <p>{ t("using this form you can add or update the record") }</p>

                        </div>
                    }
                    visible={ panelState } position="right" onHide={ onClose } blockScroll={ true }
                    style={ { width: '800px' } }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>
                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                        validationSchema={ validationSchema(t) }
                    >
                        { (props: FormikProps<TInputForm>) => {
                            return (
                                <Form onSubmit={ props.handleSubmit }>
                                    <Row className={ "mt-4" }>
                                        <Col sm={ 12 }>
                                            <Field
                                                component={ InputSingleSelectField }
                                                name="from_location_id"
                                                options={ dropdowns.locations }
                                                label={ t('from location') }
                                                placeholder={ t('pick') + ' ' + t('loading location') }
                                                panelClassName={"max-width-300px hide-p-toggler"}
                                                isRequired
                                                autoFocus
                                            />
                                        </Col>
                                        <Col sm={ 12 }>
                                            <Field
                                                component={ InputSingleSelectField }
                                                name="to_location_id"
                                                options={ dropdowns.locations }
                                                label={ t('to location') }
                                                placeholder={ t('pick') + ' ' + t('unloading location') }
                                                panelClassName={"max-width-300px hide-p-toggler"}
                                                isRequired
                                            />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSwitchField }
                                                   name="is_active"
                                                   label={ t("make this journey active") }
                                                   trueLabel={ t("yes") }
                                                   falseLabel={ t("no") }
                                            />
                                        </Col>
                                    </Row>
                                    <div className="filter-card-footer"
                                         style={ { width: '785px' } }>
                                        <Button type={ "submit" }
                                                tooltip={ t("to data save into database") }
                                                size={ "small" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-save me-2" />
                                            { t("let's save") }
                                        </Button>
                                        <Button type={ "button" }
                                                tooltip={ t("operation cancel") } className={ "ms-2" }
                                                size={ "small" } severity={ "danger" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                                onClick={ onClose }
                                        >
                                            <i className="pi pi-arrow-left me-2" />
                                            { t("cancel") }
                                        </Button>
                                    </div>
                                </Form>
                            );
                        } }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

//     AddJourneysToOrder.propTypes = {
//     t: PropTypes.any,
// };

export default withTranslation()(AddJourneysToOrder);
